'use client';

import type { AlertProps } from '@magicschool/spellbook/components/Alert';
import { Alert } from '@magicschool/spellbook/components/Alert';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { ToasterProps as SonnerToasterProps, ToastT } from 'sonner';
import { Toaster as Sonner, toast as sonnerToast } from 'sonner';

/**
 * Semantic states a toast can convey. Narrower than `AlertProps['status']`: a toast is ambient, so
 * its color has to map to a state rather than to visual emphasis. A message that needs brand-forward
 * weight to be noticed belongs in an `Alert`, `Banner`, or `Modal`.
 */
export type ToastStatus = 'success' | 'info' | 'warning' | 'error';

export interface ToastProps extends Pick<ToastT, 'duration' | 'position'>, Omit<AlertProps, 'status' | 'role' | 'aria-live'> {
  /** Message text to display (required). Should be concise and actionable. */
  title: string;
  /** Semantic color. Default: `success`. */
  status?: ToastStatus;
  'data-testid'?: string;
}

export type ToasterProps = Omit<SonnerToasterProps, 'closeButton' | 'icons' | 'invert' | 'richColors' | 'theme'>;

/**
 * Displays a transient notification (snackbar) that auto-dismisses after a set duration. Built on Sonner, renders an Alert component for visual consistency.
 *
 * ### When to use
 * - For action confirmations (e.g., "Copied to clipboard", "Changes saved")
 * - For non-critical error feedback (e.g., "Upload failed, please try again")
 * - For background task completion (e.g., "Export complete")
 * - For transient status updates that don't require user action
 *
 * ### When NOT to use
 * - For critical information that must be acknowledged: use `Alert` with inline placement instead
 * - For blocking confirmations that require a decision: use `Modal` instead
 * - For persistent messages that should remain visible: use `Alert` instead
 * - For form validation errors: display inline near the field instead
 *
 * ### API
 * - `title`: Message text to display (required)
 * - `status`: Semantic color: `success` (default), `info`, `warning`, `error`
 * - `duration`: Time in milliseconds before auto-dismiss. Default: `4000`. Set to `Infinity` to persist until dismissed.
 * - `position`: Screen position: `top-center` (default), `top-left`, `top-right`, `bottom-center`, `bottom-left`, `bottom-right`. Avoid changing position.
 * - `onDismiss`: Callback when the user clicks the close button. When provided, toast persists until user dismisses and shows close button.
 * - Inherits other props from `AlertProps` (e.g., `className`)
 *
 * ### Accessibility
 * - Sonner automatically announces the toast message text to screen readers via ARIA live regions
 * - `status` controls visual semantics (success, info, warning, error); screen readers only announce the message text, so include severity in the title/message if it must be conveyed (e.g., "Error: Upload failed")
 * - Keep messages short and actionable — users have limited time to read before auto-dismiss
 * - For critical errors that must be acknowledged, use `Alert` component instead
 *
 * ### Usage
 * ```tsx
 * // Basic usage (defaults to the `success` status)
 * toast({ title: 'Changes saved' });
 *
 * // Error with longer duration
 * toast({ title: 'Upload failed', status: 'error', duration: 6000 });
 *
 * // Dismissible toast (persists until closed)
 * toast({ title: 'New version available', status: 'info', onDismiss: () => console.log('dismissed') });
 * ```
 *
 * ### Additional notes
 * - Toasts always announce politely through the live region `<Toaster />` owns. The inner `Alert` is `role="presentation"` so it doesn't nest a second region, and `ToastProps` omits `role` and `aria-live`. For a message that must interrupt, use an `Alert`
 * - Requires `<Toaster />` to be mounted once in the app root on first paint; its region must exist before a toast is inserted for it to be announced
 * - When `onDismiss` is provided, duration automatically becomes `Infinity` and a close button appears
 * - Toasts stack vertically when multiple are shown; avoid showing many simultaneously
 * - There is deliberately no `primary` (purple) status: pick the status that matches the message's state, and if none fits the content belongs in an `Alert`, `Banner`, or `Modal` rather than a toast
 */
export const toast = ({
  className,
  duration = 4000,
  onDismiss,
  position = 'top-center',
  status = 'success',
  title,
  ...props
}: ToastProps) => {
  const handleDismiss = (id: string | number) => () => {
    if (onDismiss) {
      onDismiss();
      sonnerToast.dismiss(id);
    }
  };

  return sonnerToast.custom(
    (id) => (
      <Alert
        className={cn('w-80 shadow-popover z-toast', className)}
        onDismiss={onDismiss ? handleDismiss(id) : undefined}
        {...props}
        role="presentation"
        status={status}
      >
        <Alert.Title className="whitespace-normal">{title}</Alert.Title>
      </Alert>
    ),
    { duration: onDismiss ? Number.POSITIVE_INFINITY : duration, position },
  );
};

/**
 * Container component that renders toast notifications. Mount once in the app root.
 *
 * ### When to use
 * - Place once in your app's root layout to enable toast notifications
 *
 * ### When NOT to use
 * - Persistent or blocking messages: use `Alert` inline, or `Modal` when the user must respond
 * - Anywhere other than the app root: mount exactly one `Toaster`; call `toast()` from features instead
 *
 * ### API
 * - Accepts Sonner `ToasterProps` except `closeButton`, `icons`, `invert`, `richColors`, `theme` (controlled internally)
 *
 * ### Usage
 * ```tsx
 * // In your root layout
 * <ThemeProvider>
 *   <Toaster />
 *   {children}
 * </ThemeProvider>
 * ```
 *
 * @headless The container is rendered by Sonner; individual toasts render `Alert`.
 */
export const Toaster = ({ ...props }: ToasterProps) => {
  return <Sonner className="toaster group/toaster pointer-events-auto flex justify-center items-center" {...props} />;
};
